import { useCallback, useEffect, useState } from "react";
import { Box, Button, Container, Divider, Grid, Typography, useMediaQuery, useTheme } from "@mui/material";
import CustomRightImage from "@components/CustomRightImagev2";
import { InactivityHandler } from "@components/InactivityHandler";
import { RootState, useAppDispatch } from "@store/store";
import { useSelector } from "react-redux";
import { useLazyGetLoanApplicationByIdQuery, usePostValidateNextStageMutation } from "@api/applications";
import { useNavigate, useLocation } from "react-router-dom";
import { buildProductRoute } from "@helpers/funciones";
import useStatusConfig from "@hooks/useStatusConfig";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { ApiError, BofuTabDocument, DocumentResponse, FORM_STATUS_ENUM, PersonaStructure } from "@interfaces/response";
import * as Sentry from "@sentry/react";
import ButtonValidacionForm from "@components/ButtonValidacionForm";
import { useLazyGetPersonsQuery } from "@api/personasApi";
import { UUID } from "node:crypto";
import { useLazyGetDocumentAppIdQuery } from "@api/documents";
import { BOFU_TABS_CONFIG, DOCUMENTS_CATALOG } from "@helpers/constantes";
import { setParamsIdentityValidationForm } from "@store/slices/bofuSlice";
import { useLazyGetTabsConfigQuery } from "@api/catalogs";
import {
  generateDocumentConfig,
  generatePropietarioDocs,
  validateDocumentStructure,
} from "@components/TabBofu/functions";
import DocumentsTabBofu, { ValueAction } from "@components/TabBofu/DocumentsTabBofu";
import CenteredLoader from "@components/CenteredLoader";
import ButtonExpedienteForm from "@components/ButtonExpedienteForm"
import { LoanApplication } from "@interfaces/store";

const SCREEN_STATUS = "CSAED";

const CredimotoExpediente = () => {
  // hooks
  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { nextStatus } = useStatusConfig(SCREEN_STATUS);

  // redux hooks
  const application = useSelector((state: RootState) => state.register.application);
  const accountUser = useSelector((state: RootState) => state.register.account_user);

  // api hooks
  const [getLoanApplicationById] = useLazyGetLoanApplicationByIdQuery();
  const [triggerPostNextStage] = usePostValidateNextStageMutation();
  const [getPersonsByLoandId] = useLazyGetPersonsQuery();
  const [getDocumentsByAppId] = useLazyGetDocumentAppIdQuery();
  const [getTabsConfig] = useLazyGetTabsConfigQuery();

  // state
  const [legalRepresentative, setLegalRepresentative] = useState<PersonaStructure | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isDisabled, setIsDisabled] = useState(true);
  const [docValidacionIdentidad, setDocValidacionIdentidad] = useState<DocumentResponse | undefined>(undefined);
  const [expedienteDocuments, setExpedienteDocuments] = useState<BofuTabDocument[]>([]);
  const [isAllDocumentsUploaded, setIsAllDocumentsUploaded] = useState(false);
  const [isReferencesCompleted, setIsReferencesCompleted] = useState<FORM_STATUS_ENUM>("PENDIENTE");
  const [isAllFormsCompleted, setIsAllFormsCompleted] = useState<boolean>(false);

  const handleContinue = async () => {
    try {
      if (!application.id) {
        throw new Error("Application ID is required");
      }

      if (!nextStatus) {
        throw new Error("Next status is required");
      }

      setIsLoading(true);

      await triggerPostNextStage({
        applicationId: application.id,
        statusCode: nextStatus.code,
        username: `TOFU - ${accountUser.email}`,
      });

      navigate(nextStatus.url);
    } catch (error) {
      dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
      Sentry.captureException(error, {
        extra: {
          component: "ExpedientePF-handleContinue",
        },
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClickIdentityValidation = () => {
    if (!legalRepresentative?.id) {
      return;
    }

    dispatch(
      setParamsIdentityValidationForm({
        person_id: legalRepresentative?.id,
        person_type: "REP_LEGAL_PF",
      }),
    );
    setTimeout(() => {
      navigate(buildProductRoute(location.pathname, "validacion_identidad_pf"));
    }, 200);
  };

  const handleUpdateDocument = (document: BofuTabDocument, newValue: DocumentResponse | undefined) => {
    const copy = [...expedienteDocuments];
    const documentIndex = copy.findIndex((doc) => doc.file_category_code === document.file_category_code);
    if (documentIndex !== -1) {
      copy[documentIndex].file_value = newValue;
    }
    setExpedienteDocuments(copy);
  };

  const handleUpdateDocumentMultiple = (
    document: BofuTabDocument,
    newValue: DocumentResponse | undefined,
    valueAction: ValueAction | undefined,
    valueIndex: number | undefined,
  ) => {
    if (valueAction === "add") {
      const copy = [...expedienteDocuments];
      const documentIndex = copy.findIndex((doc) => doc.file_category_code === document.file_category_code);
      if (documentIndex !== -1) {
        copy[documentIndex].list_values.push(newValue);
      }
      setExpedienteDocuments(copy);
    } else if (valueAction === "remove") {
      const copy = [...expedienteDocuments];
      const documentIndex = copy.findIndex((doc) => doc.file_category_code === document.file_category_code);
      if (documentIndex !== -1) {
        copy[documentIndex].list_values.splice(valueIndex as number, 1);
      }
      setExpedienteDocuments(copy);
    } else if (valueAction === "update") {
      const copy = [...expedienteDocuments];
      const documentIndex = copy.findIndex((doc) => doc.file_category_code === document.file_category_code);
      if (documentIndex !== -1) {
        copy[documentIndex].list_values[valueIndex as number] = newValue;
      }
      setExpedienteDocuments(copy);
    }
  };

  const checkIfAllDocumentsAreUploaded = useCallback(() => {
    return expedienteDocuments.every((doc) => doc.file_value !== undefined);
  }, [expedienteDocuments]);

  const fetchInitialData = useCallback(async () => {
    try {
      if (!application.id || !application.client_details_id) {
        throw new Error("Application not found");
      }

      setIsLoading(true);

      const [loanApplication, persons, tabsConfig, documents] = await Promise.all([
        getLoanApplicationById(application.id).unwrap(),
        getPersonsByLoandId({ loanApplicationId: application.id }).unwrap(),
        getTabsConfig({ taxcode: application.type }).unwrap(),
        getDocumentsByAppId({ loanApplicationId: application.id, status: true }).unwrap(),
      ]);

      if (!loanApplication) {
        throw new Error("Loan application not found");
      }

      if (persons.length === 0) {
        throw new Error("Persons not found");
      }

      const tab_representante_pf = tabsConfig.find(
        (tab) => tab.tabs_bofu.code === BOFU_TABS_CONFIG.tab_representante_pf.code,
      );

      const valid_tab_documents: BofuTabDocument[] = [];

      tab_representante_pf?.documents
        .filter((item) => validateDocumentStructure(item))
        .forEach((docItem) => {
          const document_value = generateDocumentConfig({
            docItem,
            docs_account: [],
            docs_aval: [],
            clientDetailsId: application.client_details_id as UUID,
            aval: undefined,
          });

          valid_tab_documents.push(document_value);
        });

      // identify documents
      const docs_person = documents.filter((doc) => doc.person_id === persons[0].id);
      const doc_validacion_identidad = docs_person.find(
        (doc) => doc.file_code === DOCUMENTS_CATALOG.validacion_identidad.code,
      );

      const representante_documents = generatePropietarioDocs({
        personItem: persons[0],
        valid_documents: valid_tab_documents,
        documents_uploaded: docs_person,
        clientDetailsId: application.client_details_id as UUID,
      });

      setExpedienteDocuments(representante_documents);
      setDocValidacionIdentidad(doc_validacion_identidad);
      setLegalRepresentative(persons[0]);
      setIsDisabled(false);
      setIsReferencesCompleted(loanApplication.references_status);
      validateAllFormsCompleted(loanApplication);
    } catch (error) {
      dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
      Sentry.captureException(error, {
        extra: {
          component: "ExpedientePF-fetchInitialData",
        },
      });
    } finally {
      setIsLoading(false);
    }
  }, [
    application.client_details_id,
    application.id,
    application.type,
    dispatch,
    getDocumentsByAppId,
    getLoanApplicationById,
    getPersonsByLoandId,
    getTabsConfig,
  ]);

  // check if all documents are uploaded
  useEffect(() => {
    const all_documents_uploaded = checkIfAllDocumentsAreUploaded();
    setIsAllDocumentsUploaded(all_documents_uploaded);
  }, [checkIfAllDocumentsAreUploaded]);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  const validateAllFormsCompleted = (application: LoanApplication) => {
    setIsAllFormsCompleted(application.references_status === "COMPLETO");
  };
  

  return (
    <Grid container height="100%">
      <Grid item xs={12} md={8} mt={2}>
        <InactivityHandler />
        {isLoading ? (
          <CenteredLoader />
        ) : (
          <Box
            sx={{
              mt: 2,
              mx: 4,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Typography variant={"tofu_title"} textAlign="center" color={"primary.dark"}>
              Expediente
            </Typography>

            <Container maxWidth="sm" sx={{ mt: isMobileOrTablet ? 3 : 4, textAlign: "center" }}>
              <Typography
                sx={{ color: "primary.dark", mb: 1 }}
                variant={isMobileOrTablet ? "body2" : "body1"}
                textAlign="center"
                fontWeight={500}
              >
                Estas a punto de finalizar, únicamente necesitamos tu validación de identidad y los siguientes
                documentos.
              </Typography>

              <Box mt={2} display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="body1" sx={{ flex: 1, textAlign: "left" }} color={theme?.palette?.primary?.dark}>
                  Validación de identidad
                </Typography>

                <ButtonValidacionForm
                  isCompleted={legalRepresentative?.identity_validation_completed}
                  documentStatus={docValidacionIdentidad?.validated}
                  onClick={() => handleClickIdentityValidation()}
                />
              </Box>

              <Box mt={2} display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="body1" sx={{ flex: 1, textAlign: "left" }} color={theme?.palette?.primary?.dark}>
                  Referencias
                </Typography>

                <ButtonExpedienteForm
                  formStatus={isReferencesCompleted}
                  onClick={() => navigate(buildProductRoute(location.pathname, "referencias_personales"))}
                />
              </Box>

              <Divider sx={{ backgroundColor: "#A3D4E8", mt: 2 }} />

              <DocumentsTabBofu
                inputPrefix={`representante_legal`}
                tabCode={BOFU_TABS_CONFIG.tab_representante_pf.code}
                documents={expedienteDocuments}
                onChange={({ document, newValue }) => handleUpdateDocument(document, newValue)}
                onChangeMultiple={({ document, newValue, valueAction, valueIndex }) =>
                  handleUpdateDocumentMultiple(document, newValue, valueAction, valueIndex)
                }
              />

              <Box display="flex" justifyContent="center" mt={isMobileOrTablet ? 2 : 3} mb={2}>
                <Button
                  type="submit"
                  variant="default"
                  disabled={
                    isDisabled ||
                    isLoading ||
                    expedienteDocuments.length === 0 ||
                    !isAllDocumentsUploaded ||
                    !docValidacionIdentidad ||
                    !isAllFormsCompleted
                  }
                  onClick={handleContinue}
                >
                  Continuar
                </Button>
              </Box>
            </Container>
          </Box>
        )}
      </Grid>

      {!isMobileOrTablet && (
        <Grid item xs={12} md={4}>
          <CustomRightImage />
        </Grid>
      )}
    </Grid>
  );
};

export default CredimotoExpediente;
